/**
 * ProvenanceIndicator — traza el origen de un artículo hasta el mensaje de
 * WhatsApp del cliente, o lo marca como agregado manual.
 *
 * Decisiones de diseño:
 * - "El cliente es la fuente de verdad": todo artículo debe poder responder
 *   ¿de dónde salió? Este indicador es esa respuesta, siempre visible en la fila.
 * - Con fuente → botón fantasma diminuto que salta al mensaje en el panel de
 *   chat (onJump). Es acción secundaria: pequeño a propósito, pero con área de
 *   toque ampliada vía padding y focus-ring visible.
 * - Sin fuente + isManual → marcador ámbar NO clickeable: una advertencia
 *   pasiva ("Agregado manual"), no una acción. El operador debe notar que ese
 *   artículo no tiene respaldo del cliente.
 * - Hora corta (HH:MM hoy, DD/MM si es otro día) — suficiente para orientar,
 *   el detalle completo vive en el hilo de chat.
 */
import { MessageSquareQuote, PenLine } from 'lucide-react';

const shortTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const ProvenanceIndicator = ({ sourceMessageId, timestamp, isManual, onJump }) => {
  if (sourceMessageId) {
    return (
      <button
        type="button"
        onClick={() => onJump && onJump(sourceMessageId)}
        aria-label="Ver mensaje de origen en el chat"
        title="Ver mensaje de origen en el chat"
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px]
                   hover:bg-[var(--nav-active-bg)]
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]
                   disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ color: 'var(--text-tertiary)' }}
      >
        <MessageSquareQuote size={12} aria-hidden="true" />
        <span>{shortTime(timestamp)}</span>
      </button>
    );
  }

  if (isManual) {
    return (
      <span
        aria-label="Artículo agregado manualmente, sin referencia del cliente"
        title="Artículo agregado manualmente, sin referencia del cliente"
        className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium"
        style={{
          background: 'var(--amber-soft)',
          color: 'var(--amber)',
          borderColor: 'var(--amber)',
        }}
      >
        <PenLine size={11} aria-hidden="true" />
        Agregado manual
      </span>
    );
  }

  return null;
};

export default ProvenanceIndicator;

/*
Ejemplo de uso (en una fila de artículo):
  <ProvenanceIndicator
    sourceMessageId={item.source_message_id}
    timestamp={mensajeOrigen?.timestamp}
    isManual={item.no_client_reference}
    onJump={(msgId) => scrollThreadToMessage(msgId)}
  />
*/
